import React, { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { CVRParticipant } from '../../types'
import { participantRowKey } from '../../utils/participantKey'
import { Section } from '../ui'
import ParticipantRow from './ParticipantRow'

/**
 * Generic, collapsed-by-default section for "secondary" roles on the
 * profile page (Direktion, Bestyrelse, Revision, Andre roller). Counts
 * are shown in the header so the user gets a sense of the section's
 * weight before expanding.
 */
interface RoleListSectionProps {
  title: string
  description?: React.ReactNode
  participants: CVRParticipant[]
  /** Hide section entirely when there are no entries. */
  hideWhenEmpty?: boolean
  /** Begin expanded — for sections worth surfacing by default. */
  defaultOpen?: boolean
  /** Tighter section + list spacing. */
  compact?: boolean
}

const RoleListSection: React.FC<RoleListSectionProps> = ({
  title,
  description,
  participants,
  hideWhenEmpty = true,
  defaultOpen = false,
  compact = false,
}) => {
  const [open, setOpen] = useState(defaultOpen)

  if (participants.length === 0 && hideWhenEmpty) return null

  const active = participants.filter((p) => p.role.isActive)
  const historic = participants.filter((p) => !p.role.isActive)

  return (
    <Section
      title={title}
      description={description}
      compact={compact}
      trailing={
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className={`inline-flex items-center gap-1 text-ink-700 hover:text-ink ${
            compact ? 'text-xs' : 'text-sm'
          }`}
        >
          <span>
            {active.length} aktuel{active.length === 1 ? '' : 'le'}
            {historic.length > 0 && (
              <span className="text-ink-500">
                {' · '}
                {historic.length} historisk{historic.length === 1 ? '' : 'e'}
              </span>
            )}
          </span>
          <ChevronDown
            aria-hidden
            className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>
      }
    >
      {open && (
        <>
          {active.length > 0 && (
            <ul className="list-none">
              {active.map((p, i) => (
                <ParticipantRow
                  key={participantRowKey('rl-active', p, i)}
                  participant={p}
                  variant="role"
                />
              ))}
            </ul>
          )}

          {historic.length > 0 && (
            <details className={compact ? 'mt-2 group' : 'mt-4 group'}>
              <summary className="cursor-pointer text-sm text-ink-700 hover:text-ink select-none list-none inline-flex items-center gap-1">
                <ChevronDown
                  aria-hidden
                  className="h-3 w-3 transition-transform group-open:rotate-180"
                />
                Vis historiske ({historic.length})
              </summary>
              <ul
                className={
                  compact
                    ? 'list-none mt-2 border-l-2 border-rule pl-3'
                    : 'list-none mt-3 border-l-2 border-rule pl-4'
                }
              >
                {historic.map((p, i) => (
                  <ParticipantRow
                    key={participantRowKey('rl-historic', p, i)}
                    participant={p}
                    variant="role"
                    forceHistoric
                  />
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </Section>
  )
}

export default RoleListSection
